import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, ClipboardCheck, Shield, Users, CheckCircle2, Phone, Mail, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingCTAs from '../components/FloatingCTAs';
import AddressAutocomplete from '../components/AddressAutocomplete';
import ImageUploader from '../components/ImageUploader';

export default function CommercialServices() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    property_type: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    message: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, String(v));
      });
      (photos || []).slice(0, 5).forEach((file) => fd.append('photos', file));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commercial-inquiry/submit`, {
        method: 'POST',
        body: fd,
      });

      if (response.ok) {
        toast.success('Thank you! We\'ll contact you within 24 hours to discuss your project.');
        setFormData({ name: '', company: '', email: '', phone: '', property_type: '', address: '', city: '', state: '', zip_code: '', message: '' });
        setPhotos([]);
      } else {
        toast.error('Something went wrong. Please call us directly at (407) 785-0642.');
      }
    } catch (error) {
      toast.error('Failed to submit. Please call us at (407) 785-0642.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white">
      <Navbar />
      
      {/* Hero Section with Aerial Photo Background */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_eliteroofing/artifacts/8suhu65b_dji_fly_20210629_121822_595_1671460663628_photo_optimized%204.JPG"
            alt="Commercial property aerial view"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/75 to-slate-900/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Commercial Roofing & Exterior Services for Multi-Family & Commercial Properties
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-8 leading-relaxed">
              We partner with Property Managers, HOAs, CAMs, LCAMs, and Business Owners to deliver dependable roofing and exterior solutions for condominiums, townhomes, apartment complexes, and commercial buildings across Central Florida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToForm}
                className="h-14 px-8 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                data-testid="request-inspection-btn"
              >
                Request a Commercial Inspection
              </Button>
              <Button 
                onClick={scrollToForm}
                variant="outline"
                className="h-14 px-8 bg-white hover:bg-slate-50 text-slate-900 text-lg font-semibold border-2"
                data-testid="schedule-walkthrough-btn"
              >
                Schedule a Walkthrough
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who We Serve
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We understand the unique requirements of commercial property management and deliver solutions that meet your compliance and operational needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <Building2 className="h-8 w-8" />, title: 'Property Managers', desc: 'Streamlined vendor management and reliable service delivery' },
              { icon: <Users className="h-8 w-8" />, title: 'Homeowners Associations (HOAs)', desc: 'Board-ready proposals and transparent communication' },
              { icon: <ClipboardCheck className="h-8 w-8" />, title: 'CAMs & LCAMs', desc: 'Compliance-focused documentation and vendor coordination' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Commercial Property Owners', desc: 'Long-term maintenance partnerships and capital planning' },
              { icon: <Building2 className="h-8 w-8" />, title: 'Apartment & Multi-Family Communities', desc: 'Multi-building coordination and resident-friendly scheduling' },
              { icon: <Shield className="h-8 w-8" />, title: 'Mixed-Use Properties', desc: 'Comprehensive exterior services for complex properties' }
            ].map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <div className="mb-4 text-[hsl(var(--primary))]">
                      {client.icon}
                    </div>
                    <CardTitle className="text-xl mb-2">{client.title}</CardTitle>
                    <CardDescription className="text-slate-600">{client.desc}</CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-12 text-center"
          >
            <div className="inline-block bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Our Experience Includes:</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Board approval processes</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vendor compliance requirements</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">COIs & additional insured</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Multi-building scopes</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Property Types We Service
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              'Multi-Family Properties',
              'Townhome Communities',
              'Condominiums',
              'Apartment Complexes',
              'Commercial & Mixed-Use Buildings'
            ].map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 bg-slate-50 rounded-xl border-2 border-slate-200 hover:border-[hsl(var(--primary))] hover:shadow-lg transition-all"
              >
                <Building2 className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--primary))]" />
                <h3 className="text-lg font-semibold text-slate-900">{type}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              All services coordinated and managed through a single point of contact for your convenience.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Roofing Services */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-2xl mb-4 text-[hsl(var(--primary))]">Roofing Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      'Commercial roof repairs',
                      'Preventative maintenance programs',
                      'Leak detection & repair',
                      'New roof installations',
                      'Re-roofing and replacements',
                      'Storm damage assessment',
                      'Emergency tarping services'
                    ].map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Exterior & Property Services */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-2xl mb-4 text-[hsl(var(--primary))]">Exterior & Property Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {[
                      'Stucco repairs & restoration',
                      'Exterior painting',
                      'Concrete work & pavers',
                      'Fencing installation & repair',
                      'Landscaping coordination',
                      'Tree removal services',
                      'Junk & debris removal',
                      'Screen enclosure repairs'
                    ].map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How We Work
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our streamlined process ensures efficiency, transparency, and quality at every step.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Property Walkthrough & Documentation', desc: 'Comprehensive site assessment with detailed photo documentation' },
              { step: '2', title: 'Clear Scope of Work', desc: 'Detailed proposal outlining all work, timelines, and costs' },
              { step: '3', title: 'Board-Ready Proposals', desc: 'Professional documentation ready for board approval' },
              { step: '4', title: 'Licensed & Insured Execution', desc: 'Work performed by properly insured and licensed contractors' },
              { step: '5', title: 'Completion Reporting', desc: 'Final documentation with before/after photos and warranty information' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--primary))] text-white text-2xl font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Elite Roofing */}
      <section className="py-16 md:py-24 bg-[hsl(var(--primary))] text-white">
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Elite Roofing & Construction
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: <Building2 className="h-8 w-8" />, title: 'Commercial-Focused', desc: 'Specialized in multi-family and commercial properties' },
              { icon: <ClipboardCheck className="h-8 w-8" />, title: 'HOA & Management Experience', desc: 'We understand your compliance and documentation needs' },
              { icon: <Shield className="h-8 w-8" />, title: 'Fully Insured', desc: 'Comprehensive insurance and COI management' },
              { icon: <Users className="h-8 w-8" />, title: 'Subcontractor Management', desc: 'Efficient model with vetted, licensed subcontractors' },
              { icon: <CheckCircle2 className="h-8 w-8" />, title: 'Transparent Communication', desc: 'Regular updates and accessible project management' },
              { icon: <Calendar className="h-8 w-8" />, title: 'Long-Term Partnership', desc: 'We\'re here for ongoing maintenance and capital needs' }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-slate-200">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 md:py-24 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Looking for a Dependable Commercial Roofing Partner?
            </h2>
            <p className="text-xl text-slate-600">
              Schedule an inspection or request a proposal. We'll respond within 24 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="shadow-2xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="h-12"
                        data-testid="commercial-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Company / Property Name
                      </Label>
                      <Input
                        id="company"
                        type="text"
                        placeholder="Company or property name"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="h-12"
                        data-testid="commercial-company-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="h-12"
                        data-testid="commercial-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-semibold text-slate-700 mb-2 block">
                        Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(407) 555-1234"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="h-12"
                        data-testid="commercial-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="property_type" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Property Type
                    </Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    >
                      <SelectTrigger className="h-12" data-testid="commercial-property-type-select">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multi-family">Multi-Family Property</SelectItem>
                        <SelectItem value="townhome">Townhome Community</SelectItem>
                        <SelectItem value="condominium">Condominium</SelectItem>
                        <SelectItem value="apartment">Apartment Complex</SelectItem>
                        <SelectItem value="commercial">Commercial Building</SelectItem>
                        <SelectItem value="mixed-use">Mixed-Use Property</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(text) => setFormData((s) => ({ ...s, address: text }))}
                    onPlaceSelected={(p) => setFormData((s) => ({
                      ...s,
                      address: p.formatted || p.street || s.address,
                      city: p.city || s.city,
                      state: p.state || s.state,
                      zip_code: p.zip_code || s.zip_code,
                    }))}
                    label="Property Address"
                    required={false}
                    testId="commercial-address-input"
                    placeholder="Start typing the property address..."
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="h-12" placeholder="City" data-testid="commercial-city-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })} maxLength={2} className="h-12" placeholder="FL" data-testid="commercial-state-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">Zip Code</Label>
                      <Input value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} className="h-12" placeholder="ZIP" data-testid="commercial-zip-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-sm font-semibold text-slate-700 mb-2 block">
                      Project Description / Scope
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Please describe your project needs, timeline, and any specific requirements..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="resize-none"
                      data-testid="commercial-message-textarea"
                    />
                  </div>

                  <ImageUploader
                    files={photos}
                    onChange={setPhotos}
                    maxFiles={5}
                    maxMb={10}
                    label="Attach photos (optional)"
                    hint="Property photos, scope-of-work areas, or damage. Up to 5 images, 10MB each."
                    testId="commercial-photos"
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[hsl(var(--accent))] hover:bg-[#a01830] text-white text-lg font-bold shadow-xl"
                    data-testid="commercial-submit-btn"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request a Consultation'}
                  </Button>

                  <div className="text-center space-y-3 pt-4 border-t border-slate-200">
                    <p className="text-sm text-slate-600">Or contact us directly:</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <a href="tel:+14077850642" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Phone className="h-4 w-4" />
                        (407) 785-0642
                      </a>
                      <a href="mailto:jeremy@elitercfl.com" className="flex items-center gap-2 text-[hsl(var(--primary))] hover:underline font-semibold">
                        <Mail className="h-4 w-4" />
                        jeremy@elitercfl.com
                      </a>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
      <FloatingCTAs />
    </div>
  );
}
